import { useMemo } from 'react';
import { MotionPriority } from 'pixi-live2d-display/cubism4';
import Live2DStage from '../../components/live2d/Live2DStage';
import { buildLive2dModelUrlFromParts } from '../../utils/live2dUrls';

const LIVE2D_PUBLIC = process.env.PUBLIC_URL || '';

function sceneCanvasAssetUrl(doc) {
  return doc?.imageUrl || doc?.thumbnailUrl || '';
}

function normRect(r, fallback) {
  if (!r || typeof r !== 'object') return fallback;
  const num = (v, d) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : d;
  };
  return {
    x: num(r.x, fallback.x),
    y: num(r.y, fallback.y),
    width: Math.max(0.001, num(r.width, fallback.width)),
    height: Math.max(0.001, num(r.height, fallback.height))
  };
}

const DEFAULT_BG = { x: 0, y: 0, width: 1, height: 1 };
const DEFAULT_CHAR = { x: 0.25, y: 0.12, width: 0.5, height: 0.88 };

/**
 * Read-only 16:9 scene (background + optional Live2D + optional foreground), same layout as scene editor.
 * @param {string|null} [props.slideOverlayUrl] — Background node kind=slide: 1920×1080 (or any) image in this frame, above Live2D, below foreground; Live2D hidden.
 * @param {object|null} [props.animLive2dClip] — из графа контроллера: { modelPath, motionGroup, motionIndex, motionLoop }
 * @param {() => void} [props.onAnimMotionFinished] — Cubism motionFinish (не для loop)
 */
export default function FlowSceneRenderer({
  scene,
  slideOverlayUrl = null,
  animLive2dClip = null,
  onAnimMotionFinished
}) {
  const bgDoc = scene?.backgroundAssetId;
  const fgDoc = scene?.foregroundAssetId;
  const charDoc = scene?.characterId;

  const bgUrl = sceneCanvasAssetUrl(bgDoc);
  const fgUrl = sceneCanvasAssetUrl(fgDoc);

  const backgroundRect = useMemo(() => normRect(scene?.backgroundRect, DEFAULT_BG), [scene?.backgroundRect]);
  const characterRect = useMemo(() => normRect(scene?.characterRect, DEFAULT_CHAR), [scene?.characterRect]);
  const foregroundRect = useMemo(() => normRect(scene?.foregroundRect, DEFAULT_BG), [scene?.foregroundRect]);

  const live2dModelUrl = useMemo(() => {
    const pack = charDoc?.live2dPack;
    const rel = animLive2dClip?.modelPath || charDoc?.live2dModelPath;
    return buildLive2dModelUrlFromParts(LIVE2D_PUBLIC, pack, rel);
  }, [charDoc?.live2dPack, charDoc?.live2dModelPath, animLive2dClip?.modelPath]);

  const motionGroup = animLive2dClip?.motionGroup || 'idle';
  const motionIndex = useMemo(() => {
    if (animLive2dClip) {
      const n = Number(animLive2dClip.motionIndex);
      return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
    }
    const n = Number(charDoc?.live2dIdleMotionIndex);
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
  }, [animLive2dClip, charDoc?.live2dIdleMotionIndex]);

  /** Clip задаёт loop: при наличии onComplete (`done`) в графе приходит `motionLoop: false`. */
  const motionLoop = animLive2dClip ? animLive2dClip.motionLoop !== false : true;

  const hasSlide = Boolean(slideOverlayUrl);
  const hasCharacter = Boolean(charDoc && live2dModelUrl && !hasSlide);
  const hasForeground = Boolean(fgUrl);

  return (
    <div className="pointer-events-none absolute inset-0 z-[24] flex items-center justify-center bg-black">
      <div className="relative aspect-video w-full max-w-[min(100vw,calc(100dvh*16/9))] overflow-hidden">
        <div
          className="absolute"
          style={{
            left: `${backgroundRect.x * 100}%`,
            top: `${backgroundRect.y * 100}%`,
            width: `${backgroundRect.width * 100}%`,
            height: `${backgroundRect.height * 100}%`,
            zIndex: 1
          }}
        >
          {bgUrl ? (
            <img
              src={bgUrl}
              alt=""
              draggable={false}
              className="block h-full w-full object-contain object-center"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-zinc-900 text-xs text-zinc-500" />
          )}
        </div>

        {hasCharacter ? (
          <div
            className="absolute overflow-hidden bg-transparent"
            style={{
              left: `${characterRect.x * 100}%`,
              top: `${characterRect.y * 100}%`,
              width: `${characterRect.width * 100}%`,
              height: `${characterRect.height * 100}%`,
              zIndex: 2
            }}
          >
            <div className="pointer-events-none flex h-full min-h-0 w-full min-w-0 flex-col">
              <Live2DStage
                key={live2dModelUrl}
                modelUrl={live2dModelUrl}
                motionGroup={motionGroup}
                motionIndex={motionIndex}
                motionLoop={motionLoop}
                motionGraphNodeId={animLive2dClip?.graphNodeId}
                motionReplayNonce={animLive2dClip?.replayNonce ?? 0}
                interruptPriorMotion={Boolean(animLive2dClip)}
                motionStartPriority={
                  animLive2dClip ? MotionPriority.FORCE : MotionPriority.NORMAL
                }
                onMotionFinish={onAnimMotionFinished}
                backgroundColor={0}
                backgroundAlpha={0}
                layoutVerticalAlign="bottom"
                layoutHorizontalRatio={0.5}
                layoutScaleMultiplier={1}
                className="min-h-0 min-w-0 flex-1"
              />
            </div>
          </div>
        ) : null}

        {hasForeground ? (
          <div
            className="absolute"
            style={{
              left: `${foregroundRect.x * 100}%`,
              top: `${foregroundRect.y * 100}%`,
              width: `${foregroundRect.width * 100}%`,
              height: `${foregroundRect.height * 100}%`,
              zIndex: 3
            }}
          >
            <img
              src={fgUrl}
              alt=""
              draggable={false}
              className="block h-full w-full object-contain object-center"
            />
          </div>
        ) : null}

        {hasSlide ? (
          <div
            className="pointer-events-none absolute inset-0 z-[4] flex items-center justify-center"
            aria-hidden
          >
            <img
              src={slideOverlayUrl}
              alt=""
              draggable={false}
              className="max-h-full max-w-full object-contain object-center"
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
